/**
 * @file auth.js
 * @description Manages authentication, token handling, and user session information.
 * Interacts with the backend API for login, token refresh, and fetching account details.
 */

import webApiClient from "../api/client";
import { jwtDecode } from "jwt-decode";

const ACCESS_TOKEN_KEY = "accessToken";
const REFRESH_TOKEN_KEY = "refreshToken";

/**
 * Safely decodes a JWT token.
 * @param {string | null} token - The JWT token string.
 * @returns {object | null} The decoded token payload or null if decoding fails or token is invalid.
 */
const decodeTokenSafe = token => {
    if (!token) return null;
    try {
        return jwtDecode(token);
    } catch (error) {
        console.error("Failed to decode token:", error);
        return null;
    }
};

/**
 * Stores both access and refresh tokens in localStorage.
 * @param {string} accessToken - The access token.
 * @param {string} refreshToken - The refresh token.
 */
const storeTokens = (accessToken, refreshToken) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
};

/**
 * Stores only the access token in localStorage.
 * @param {string} accessToken - The access token.
 */
const storeAccessToken = accessToken => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
};

/**
 * Retrieves the access token from localStorage.
 * @returns {string | null} The access token or null if not found.
 */
const getToken = () => {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
};

/**
 * Retrieves the refresh token from localStorage.
 * @returns {string | null} The refresh token or null if not found.
 */
const getRefreshToken = () => {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
};

const initializeAuth = async () => {
    const accessToken = localStorage.getItem("accessToken");
    const refreshToken = localStorage.getItem("refreshToken");

    if (accessToken) {
        try {
            // Verify token is still valid by getting current user
            const userData = await getCurrentUser();
            return userData;
        } catch (error) {
            // Token invalid — webApiClient interceptor already attempted refresh+retry.
            // If we're here the interceptor retry also failed, so try once more directly.
            if (refreshToken) {
                try {
                    const newToken = await refreshAccessToken(); // returns token string or null; stores token internally
                    if (newToken) {
                        return await getCurrentUser();
                    }
                } catch (refreshError) {
                    // Clear invalid tokens
                    localStorage.removeItem("accessToken");
                    localStorage.removeItem("refreshToken");
                }
            }
        }
    }
    return null;
};

/**
 * Removes access and refresh tokens from localStorage and clears the account details cache.
 */
const removeTokens = () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    accountDetailsCache = null;
};

/**
 * Attempts to refresh the access token using the stored refresh token.
 * @async
 * @returns {Promise<string | null>} The new access token if successful, otherwise null.
 */
const refreshAccessToken = async () => {
    try {
        const refreshToken = getRefreshToken();

        if (!refreshToken) {
            return null;
        }

        const response = await webApiClient.post("/api/v2/auth/refresh", {
            refreshToken,
        });

        if (response.ok && response.data.accessToken) {
            storeAccessToken(response.data.accessToken);
            return response.data.accessToken;
        }

        return null;
    } catch (error) {
        console.error("Failed to refresh token:", error);
        return null;
    }
};

/**
 * Logs in a user with email and password.
 * Stores tokens upon successful login and fetches account details.
 * @async
 * @param {string} email - The user's email.
 * @param {string} password - The user's password.
 * @returns {Promise<object>} An object indicating success status, user data, and account details.
 * Contains `{ success: boolean, user?: object, accountDetails?: object, message?: string }`.
 */
const login = async (email, password) => {
    try {
        const response = await webApiClient.post("/api/v2/auth/login", {
            email,
            password,
        });

        if (response.ok && response.data) {
            let accessTokenToDecode;

            if (response.data.accessToken && response.data.refreshToken) {
                // New format with both tokens
                storeTokens(response.data.accessToken, response.data.refreshToken);
                accessTokenToDecode = response.data.accessToken;
            } else if (typeof response.data === "string" && response.data.length > 0) {
                // Old format - single token string (backward compatibility)
                accessTokenToDecode = response.data;
                storeAccessToken(accessTokenToDecode);
            } else {
                // Unexpected response format
                console.error("Login response has unexpected token format:", response.data);
                return { success: false, message: "Login failed due to unexpected server response." };
            }

            if (!accessTokenToDecode) {
                console.error("No access token found in login response.");
                return { success: false, message: "Login failed: No token received." };
            }

            const userData = decodeTokenSafe(accessTokenToDecode);
            if (!userData) {
                console.error("Received malformed token from server after login.");
                removeTokens(); // Clean up potentially bad stored tokens
                return { success: false, message: "Login failed: Invalid token received." };
            }

            const accountDetails = userData.accountId ? await getAccountDetails(userData.accountId) : null;
            return {
                success: true,
                user: userData,
                accountDetails,
                accessToken: response.data.accessToken,
                refreshToken: response.data.refreshToken,
                assignedOrgs: response.data.assignedOrgs || null, // pass through from backend
            };
        } else {
            return {
                success: false,
                message: response.data?.message || "Login failed",
            };
        }
    } catch (error) {
        console.error("Error during login:", error);
        return {
            success: false,
            message: "Login failed. Please try again later.",
        };
    }
};

/**
 * Checks if the current user is authenticated.
 * Validates the current access token and attempts to refresh it if expired.
 * @async
 * @returns {Promise<boolean>} True if authenticated, false otherwise.
 */
const isAuthenticated = async () => {
    const currentToken = getToken();
    if (!currentToken) return false;

    const userData = decodeTokenSafe(currentToken);
    if (!userData) {
        // Token is malformed or unreadable. Consider removing it.
        // localStorage.removeItem(ACCESS_TOKEN_KEY);
        return false;
    }

    if (!userData.exp) {
        // No expiration in token, assume it's valid
        return true;
    }

    const currentTime = Date.now() / 1000;
    if (userData.exp > currentTime) {
        // Token is still valid
        return true;
    }

    // Token is expired, try to refresh
    const newToken = await refreshAccessToken();

    // If refresh successful, user is authenticated
    return !!newToken;
};

/**
 * Fetches account details for a given account ID from the backend.
 * @async
 * @param {string} accountId - The ID of the account to fetch details for.
 * @returns {Promise<object | null>} The account details object or null if fetching fails.
 */
const getAccountDetails = async accountId => {
    try {
        const response = await webApiClient.get(`/api/v2/account/profile`);

        if (response.ok) {
            return response.data.profile; // V2 wraps in { success: true, profile: {...} }
        } else {
            console.error("Failed to fetch account details:", response.problem);
            return null;
        }
    } catch (error) {
        console.error("Error fetching account details:", error);
        return null;
    }
};

/**
 * Checks if the currently authenticated user is a dance school.
 * @returns {boolean} True if the user is a dance school, false otherwise.
 */
const isDanceSchool = () => {
    // Uses safe decoding for robustness
    const userData = decodeTokenSafe(getToken());
    if (!userData) return false;

    // In your backend, you're checking organisationId for dance schools
    return userData.organisationId !== null && userData.organisationId !== undefined;
};

let accountDetailsCache = null;

/**
 * Retrieves the current user's data, including decoded token information and account details.
 * Uses a cache for account details to avoid redundant API calls.
 * @async
 * @returns {Promise<object | null>} An object containing user data and account details, or null if not authenticated.
 */
const getCurrentUser = async () => {
    const userDataFromToken = decodeTokenSafe(getToken());
    if (!userDataFromToken) return null;

    // Fetch account details if accountId exists and cache is missing or potentially stale
    // The primary cache invalidation happens on logout (removeTokens sets accountDetailsCache to null)
    if (userDataFromToken.accountId) {
        if (!accountDetailsCache) {
            // Fetch if not cached
            accountDetailsCache = await getAccountDetails(userDataFromToken.accountId);
        }
    } else {
        // No accountId in token, ensure cache is cleared if it held old data
        accountDetailsCache = null;
    }

    return {
        ...userDataFromToken,
        accountDetails: accountDetailsCache,
    };
};

/**
 * Logs out the current user by removing stored tokens.
 */
const logout = () => {
    removeTokens();
};

export { login, logout, isAuthenticated, isDanceSchool, getCurrentUser, getToken, initializeAuth };
